import React, { useState, useEffect, useRef } from 'react';
import { 
  Presentation, 
  ChevronRight, 
  ChevronLeft, 
  Sparkles,
  X,
  ShieldCheck,
  Award,
  Layers,
  CheckCircle2,
  ExternalLink,
  Download,
  Copy,
  Check,
  Printer,
  FileText,
  TrendingUp,
  DollarSign,
  Users,
  Building2,
  Globe,
  Zap,
  Target
} from 'lucide-react';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';

export interface DeckSlide {
  number: number;
  title: string;
  tagline: string;
  category: string;
  keyPoints: string[];
  metricsOrHighlights: { label: string; value: string; sub?: string }[];
  takeaway: string;
}

export const AZURAOS_PITCH_DECK_SLIDES: DeckSlide[] = [
  {
    number: 1,
    category: "Cover & Overview",
    title: "AzuraOS — Sovereign Financial OS",
    tagline: "Forward Solvency Radar & Portfolio Governance for Accelerators & Startup Cohorts",
    keyPoints: [
      "AzuraOS is the forward solvency operating system and batch governance hub giving startup accelerators, venture batches, and autonomous founders real-time financial clarity.",
      "Engineered with automated statutory tax reserve ring-fencing (IRS, HMRC, EU VAT, SARS, ATO), Multi-AI Provider BYOK (Gemini, Claude, OpenAI, DeepSeek), and an air-gapped dual-vault statement parser.",
      "Live in production at https://azuraos.app • Google for Startups Cloud Program Recipient ($2,000+ Backing) • Live F6S Accelerator Batch Pilot Listing • Global Merchant of Record via Dodo Payments.",
      "Founder & Principal Architect: Raksha Singh • Azura Seraph™ Pty Ltd • Pre-Seed Raise ($500,000 Ask)."
    ],
    metricsOrHighlights: [
      { label: "Company", value: "Azura Seraph™ Pty Ltd", sub: "Raksha Singh, Founder" },
      { label: "Platform", value: "https://azuraos.app", sub: "Live Production System" },
      { label: "Stage", value: "Pre-Seed ($500k)", sub: "Active F6S Accelerator Deal" }
    ],
    takeaway: "The sovereign financial operating system and cohort governance hub for early-stage startup ventures."
  },
  {
    number: 2,
    category: "1. Hook & Problem",
    title: "The Rearview Mirror Trap & Blind Runway Bleed",
    tagline: "Tax Liabilities Co-Mingled in Operating Cash & Zero Real-Time Cohort Visibility",
    keyPoints: [
      "The False Runway Illusion: When revenue arrives, founders see a swollen bank balance and spend on growth, unaware that 25–40% is already owed to tax authorities. They discover their true shortfall months later when tax assessments strike.",
      "Accelerator Blind Spots: Program Directors and Incubator Managers have zero real-time visibility into the cash survival, burn rate, or compliance readiness of their 20–30 batch startups until it is too late.",
      "Legacy Software Fragmentation: QuickBooks and Xero are backward-looking compliance tools built for CPAs weeks after money moves. Founders are forced to juggle 4–6 siloed apps (DocuSign, Invoicing, Spreadsheets, Bookkeeping) with zero forward solvency radar."
    ],
    metricsOrHighlights: [
      { label: "Silent Cash Bleed", value: "25–40%", sub: "Unhedged tax liabilities & FX fees" },
      { label: "Batch Blindness", value: "100% Siloed", sub: "No real-time portfolio metrics" },
      { label: "Tool Sprawl", value: "4–6 Apps", sub: "Fragmented billing & contract stack" }
    ],
    takeaway: "Startups don't die from bad ideas; they die from blind cash flow bleed and retroactive tax surprises."
  },
  {
    number: 3,
    category: "2. Solution & Platform",
    title: "Live Forward Solvency & Batch Governance",
    tagline: "Automatic Statutory Ring-Fencing, True Safe Spendable Draw & Director Dashboard",
    keyPoints: [
      "Live Forward Solvency Radar: Rather than looking backward, AzuraOS dynamically ring-fences multi-jurisdiction tax reserves (IRS, HMRC, EU VAT, SARS, ATO) and recalculates the founder's exact 'True Safe Spendable Draw' before committing a single dollar.",
      "Program Director Portfolio Governance: Accelerator managers get an aggregated, privacy-preserving dashboard to track cohort burn, revenue milestones, and financial health across all 25 batch startups.",
      "Unified Sovereign Operating System: Replaces fragmented point solutions by unifying legally binding client agreements, multi-currency proforma invoicing, and AI-assisted reconciliation into one offline-first, client-encrypted platform."
    ],
    metricsOrHighlights: [
      { label: "Solvency Radar", value: "Real-Time", sub: "True Safe Spendable Draw" },
      { label: "Batch Dashboard", value: "25 Seats/Cohort", sub: "Portfolio milestone tracking" },
      { label: "Statutory Engine", value: "5 Global Regimes", sub: "IRS, HMRC, SARS, ATO, EU VAT" }
    ],
    takeaway: "Transforming reactive accounting into an active forward-looking cash protection and governance shield."
  },
  {
    number: 4,
    category: "3. Multi-AI Architecture",
    title: "Multi-AI Provider BYOK & Air-Gapped Engine",
    tagline: "Gemini, Claude, OpenAI & DeepSeek with Local Heuristic Fallbacks & Zero-Plaid Ingestion",
    keyPoints: [
      "Multi-Provider BYOK (Bring Your Own Key): Enterprise teams and power founders plug in their own API keys across Google Gemini, Anthropic Claude, OpenAI, and DeepSeek, powered by an intelligent server AI dispatcher with automated failover.",
      "Zero-Latency Local Heuristic Fallbacks: If external APIs time out or quota is depleted, AzuraOS's deterministic local engine computes margins, compliance checks, and audit trails locally with 100% uptime.",
      "0.05-Second Air-Gapped Statement Ingestion: Dual-vault in-memory parsing for PDFs and CSVs with zero bank password scraping (deliberate No-Plaid architecture) ensuring total cryptographic data sovereignty."
    ],
    metricsOrHighlights: [
      { label: "Multi-AI Models", value: "4 Major Providers", sub: "Gemini, Claude, OpenAI, DeepSeek" },
      { label: "Failover Engine", value: "Deterministic", sub: "100% offline heuristic fallback" },
      { label: "Parser Speed", value: "< 0.05s", sub: "Air-gapped dual-vault security" }
    ],
    takeaway: "Unmatched technical resilience: flexible multi-model AI combined with client-side encrypted sovereignty."
  },
  {
    number: 5,
    category: "4. Distribution & Moat",
    title: "The B2B2C Accelerator Distribution Flywheel",
    tagline: "1 Deal = 25 Locked-in Startups • Unfair Moat vs Single-User D2C Tools",
    keyPoints: [
      "The B2B2C Cohort Wedge: Instead of burning capital on noisy individual D2C ads, we partner directly with Accelerators, Incubators, and Venture Studios. One $750 pilot deal provisions 25 active startups on Day 1.",
      "Permanent SaaS Retention: When cohort founders graduate from their 30-day batch, AzuraOS remains their permanent operating system ($29–$79/mo), creating natural long-term expansion ARR.",
      "3-Layer Defensible Moat: (1) B2B2C institutional distribution pipeline; (2) Proprietary multi-jurisdiction statutory tax logic; (3) Client-side cryptographic data sovereignty with zero SaaS lock-in."
    ],
    metricsOrHighlights: [
      { label: "Distribution Wedge", value: "B2B2C Batches", sub: "25 startups acquired per sale" },
      { label: "Post-Grad Retention", value: "$29–$79/mo", sub: "Permanent founder operating stack" },
      { label: "Defensible Moat", value: "3-Layer Shield", sub: "Distribution, Tax Engine, Privacy" }
    ],
    takeaway: "High-velocity acquisition loops embedded directly into institutional accelerator pipelines."
  },
  {
    number: 6,
    category: "5. Business Model & Unit Economics",
    title: "High-Margin Dual Revenue Model",
    tagline: "$750 Accelerator Pilots + $29–$79/mo Founder SaaS • >85% Gross Margins",
    keyPoints: [
      "Institutional Cohort Pilots: $750 one-time 30-day structured pilot for up to 25 seats ($4,750 real savings vs $5,500 enterprise value), listed actively on F6S.",
      "Founder SaaS Subscriptions: Post-pilot and direct solo founder plans at $29/mo ($290/yr) global / R499/mo localized, stepping up to $79/mo for advanced multi-entity tiers.",
      "Ultra-Lean Serverless Economics: Client-side cryptographic processing and Google Cloud serverless architecture deliver >85% gross margins with negligible token overhead."
    ],
    metricsOrHighlights: [
      { label: "Cohort Pilot Tier", value: "$750 / Batch", sub: "25 seats + Director Dashboard" },
      { label: "Founder SaaS Tier", value: "$29–$79 / mo", sub: "Recurring subscription engine" },
      { label: "Gross Margins", value: "> 85%", sub: "High capital-efficient unit economics" }
    ],
    takeaway: "Predictable, high-margin revenue driven by upfront B2B batch sales and compounding SaaS ARR."
  },
  {
    number: 7,
    category: "6. Traction & Validation",
    title: "De-Risked Execution & Live Pipeline",
    tagline: "Google Cloud Backed • Live F6S Listing • Battle-Tested Alpha Operations",
    keyPoints: [
      "Institutional Program Backing: Selected for the Google for Startups Cloud Program with $2,000+ in cloud infrastructure credits.",
      "Live F6S Accelerator Deal: Active featured software deal targeting accelerator directors worldwide with automated 1-click batch redemption (?code=COHORT).",
      "Production-Ready Infrastructure: Live at https://azuraos.app with integrated Merchant of Record (Dodo Payments), Firebase real-time sync, and multi-currency global billing."
    ],
    metricsOrHighlights: [
      { label: "Google for Startups", value: "$2,000+ Backing", sub: "Cloud program tier recipient" },
      { label: "F6S Pilot Pipeline", value: "Live Deal", sub: "Exclusive 25-seat batch package" },
      { label: "Production System", value: "azuraos.app", sub: "Live contract-to-cash platform" }
    ],
    takeaway: "Completely de-risked technical foundation with an active institutional go-to-market pipeline."
  },
  {
    number: 8,
    category: "7. Founder & Execution",
    title: "Technical Mastery & Relentless Shipping",
    tagline: "Raksha Singh — Solo Architect with Systems Analysis & Full-Stack Execution",
    keyPoints: [
      "Full-Stack Founder-Architect: Raksha Singh engineered and deployed AzuraOS end-to-end, combining systems analysis, luxury operations rigor, and deep reactive software engineering.",
      "100% Bootstrapped Grit: Built and shipped production software with extreme capital efficiency, zero external dilution, and a clean cap table.",
      "Strategic GTM Team Expansion: Actively hiring a Partnerships Lead and Systems Engineer to scale accelerator integrations and enterprise pilots."
    ],
    metricsOrHighlights: [
      { label: "Founder", value: "Raksha Singh", sub: "Founder & Principal Architect" },
      { label: "Capital Efficiency", value: "100% In-House", sub: "Zero wasted investor burn" },
      { label: "Cap Table", value: "100% Clean", sub: "Ready for institutional investment" }
    ],
    takeaway: "High-velocity builder with deep domain conviction and proven ability to ship complex software solo."
  },
  {
    number: 9,
    category: "8. The Ask & Road to May 2027",
    title: "The Ask & 18-Month Scaling Roadmap",
    tagline: "Raising $500,000 Pre-Seed • 25–35 Accelerators • $25k–$40k MRR Target",
    keyPoints: [
      "The Ask ($500,000 Pre-Seed): Funding dedicated to scaling B2B accelerator partnerships, developer integrations (Open Banking / Plaid), and high-touch cohort customer success.",
      "May 2027 Milestone: 25–35 global accelerator cohorts onboarded (600+ active founders) reaching $25k–$40k MRR ($300k–$480k ARR run-rate).",
      "Venture Scale: Establishing AzuraOS as the default sovereign financial operating system for early-stage startup batches globally."
    ],
    metricsOrHighlights: [
      { label: "Pre-Seed Ask", value: "$500,000", sub: "GTM scale & banking integrations" },
      { label: "Target Partners", value: "25–35 Cohorts", sub: "600+ active startups by May 2027" },
      { label: "Target ARR", value: "$300k–$480k", sub: "$25k–$40k monthly run-rate" }
    ],
    takeaway: "Join us in defining the financial operating standard for the next generation of global startups."
  },
  {
    number: 10,
    category: "9. Executive Synthesis",
    title: "Elevator & Institutional Pitch Reference",
    tagline: "30-Second Elevator Pitch & 2-Minute Cohort Governance Pitch",
    keyPoints: [
      "30-Second Elevator Pitch: Legacy accounting tools look in the rearview mirror, leaving tax liabilities co-mingled in operating balances until founders face sudden cash depletion. AzuraOS is a live forward solvency radar that automatically ring-fences multi-jurisdiction statutory reserves (IRS, HMRC, EU VAT, SARS, ATO) and computes True Safe Spendable Draw in real-time. For accelerators, our 25-seat cohort dashboard gives program directors real-time batch visibility and audit exports without violating founder privacy.",
      "2-Minute Sales Pitch: If you run an accelerator or manage startup batches, tracking portfolio financial health is a manual nightmare of outdated spreadsheets. For batch founders, spending blindly without ring-fencing tax reserves kills runway. AzuraOS fixes both sides: one $750 pilot provisions 25 founders with complete multi-currency invoicing, client agreements, and AI statement reconciliation, while the program director tracks real-time burn and revenue milestones. Backed by Google for Startups and live on F6S, AzuraOS turns bookkeeping into an active survival shield.",
      "Contact & Diligence: Direct founder channel at founder@azuraos.app • Live production system at https://azuraos.app."
    ],
    metricsOrHighlights: [
      { label: "Elevator Pitch", value: "30 Seconds", sub: "Forward Solvency & Cohort Radar" },
      { label: "Sales Pitch", value: "2 Minutes", sub: "25-Seat Pilot & Director Suite" },
      { label: "Contact", value: "founder@azuraos.app", sub: "Live at https://azuraos.app" }
    ],
    takeaway: "Comprehensive investor and customer narrative engineered for rapid diligence and high-conviction backing."
  }
];

export const AZURA_SERAPH_FULL_PITCH_DOCUMENT = `===================================================================
AZURA SERAPH™ // AZURAOS — INVESTOR & ACCELERATOR PITCH DECK
Company: AzuraOS (Azura Seraph™ Pty Ltd)
Founder & Principal Architect: Raksha Singh (founder@azuraos.app)
Live Production URL: https://azuraos.app
Ecosystem: Google for Startups Cloud Program ($2,000+ Backing) • F6S Accelerator Deal • Dodo Payments MoR
Target Round: $500,000 Pre-Seed Round
===================================================================

[A] 30-SECOND ELEVATOR PITCH
-------------------------------------------------------------------
Legacy accounting tools look in the rearview mirror, leaving statutory tax liabilities co-mingled in operating cash until founders face sudden cash depletion. AzuraOS is a live forward solvency radar that automatically ring-fences multi-jurisdiction tax reserves (IRS, HMRC, EU VAT, SARS, ATO) and recalculates your True Safe Spendable Draw in real-time before you commit a dollar. For accelerators and incubators, our 25-seat cohort governance dashboard gives program directors real-time batch health metrics and 1-click audit exports without compromising individual founder privacy.

[B] 2-MINUTE COHORT GOVERNANCE PITCH
-------------------------------------------------------------------
If you run an accelerator or startup incubator, tracking the financial survival of your batch is an administrative nightmare of outdated spreadsheets. For batch founders, navigating cross-border invoicing, contracts, and unhedged tax liabilities leads to silent runway bleed.

AzuraOS bridges both worlds: with our $750 30-day cohort pilot, program directors provision 25 startup founders with an all-in-one financial workstation—including multi-currency proforma invoicing, legally binding client agreements, Multi-AI Provider BYOK (Gemini, Claude, OpenAI, DeepSeek), and a 0.05-second air-gapped statement reconciler with zero bank credential scraping. 

Meanwhile, the director monitors aggregated cohort burn rates, milestone velocity, and tax readiness from one centralized dashboard. Backed by Google for Startups and live on F6S, AzuraOS turns financial operations into an active survival shield for early-stage ventures.

[C] 10-SLIDE INVESTOR PITCH DECK SUMMARY
-------------------------------------------------------------------
1. COVER & OVERVIEW: Sovereign Financial OS & Cohort Governance Hub for Accelerators & Founders.
2. THE PROBLEM: The Rearview Mirror Trap & Co-Mingled Tax Runway Depletion.
3. THE SOLUTION: Live Forward Solvency Radar, Statutory Ring-Fencing & Director Dashboard.
4. PRODUCT & MULTI-AI: Multi-Provider BYOK (Gemini/Claude/OpenAI/DeepSeek) + 0.05s Air-Gapped Parser.
5. DISTRIBUTION & MOAT: B2B2C Accelerator Distribution Flywheel (1 sale = 25 locked-in startups).
6. BUSINESS MODEL: $750 Cohort Pilots + $29–$79/mo Founder SaaS (>85% Gross Margins).
7. TRACTION: Google for Startups Cloud Program ($2k+), Live F6S Deal, Battle-tested Alpha.
8. FOUNDING TEAM: Raksha Singh (Solo Systems Architect, 100% Bootstrapped with Clean Cap Table).
9. THE ASK & MILESTONES: Raising $500,000 Pre-Seed to scale 25–35 accelerator cohorts by May 2027 ($25k–$40k MRR).
10. CONTACT & DILIGENCE: founder@azuraos.app • https://azuraos.app
===================================================================`;

export const ALTALAB_FULL_PITCH_DOCUMENT = AZURA_SERAPH_FULL_PITCH_DOCUMENT;

interface PitchDeckModalProps {
  isOpen: boolean;
  onClose: () => void;
  showToast?: (msg: string, type?: 'success' | 'info') => void;
  slides?: DeckSlide[];
  deckTitle?: string;
  companyName?: string;
  founderName?: string;
  contactEmail?: string;
}

export function PitchDeckModal({
  isOpen,
  onClose,
  showToast,
  slides,
  deckTitle,
  companyName,
  founderName,
  contactEmail,
}: PitchDeckModalProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [copiedText, setCopiedText] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const slideCardRef = useRef<HTMLDivElement>(null);

  const activeSlides = slides && slides.length > 0 ? slides : AZURAOS_PITCH_DECK_SLIDES;
  const totalSlides = activeSlides.length;
  const currentSlide = activeSlides[currentSlideIndex] || activeSlides[0];

  useEffect(() => {
    if (isOpen) {
      setCurrentSlideIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        if (currentSlideIndex < totalSlides - 1) {
          setCurrentSlideIndex(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft') {
        if (currentSlideIndex > 0) {
          setCurrentSlideIndex(prev => prev - 1);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentSlideIndex, totalSlides, onClose]);

  if (!isOpen) return null;

  const effectiveTitle = deckTitle || 'Azura Seraph™ — AzuraOS Pitch Deck';
  const effectiveCompany = companyName || 'Azura Seraph™ Pty Ltd';

  const handleNext = () => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const handleCopyFullBriefing = () => {
    navigator.clipboard.writeText(ALTALAB_FULL_PITCH_DOCUMENT);
    setCopiedText(true);
    if (showToast) showToast('📋 Complete Azura Seraph™ Pitch Deck copied to clipboard!', 'success');
    setTimeout(() => setCopiedText(false), 3000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const page1El = document.getElementById('azura-pitch-page-1');
      const page2El = document.getElementById('azura-pitch-page-2');
      if (!page1El || !page2El) {
        throw new Error('PDF Export Page elements not found');
      }

      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas-pro')
      ]);

      // Configure offscreen elements to ensure accurate snapshot rendering
      const capturePage = async (element: HTMLElement) => {
        return await html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          onclone: (clonedDoc) => {
            sanitizeClonedDocForPdf(clonedDoc);
            const wrapper = clonedDoc.getElementById('azura-pitch-deck-pdf-canvas-wrapper');
            if (wrapper) {
              wrapper.style.position = 'static';
              wrapper.style.left = '0';
              wrapper.style.top = '0';
              wrapper.style.display = 'block';
              wrapper.style.visibility = 'visible';
              wrapper.style.opacity = '1';
              wrapper.style.overflow = 'visible';
            }
            const p1 = clonedDoc.getElementById('azura-pitch-page-1');
            if (p1) {
              p1.style.position = 'static';
              p1.style.visibility = 'visible';
              p1.style.opacity = '1';
            }
            const p2 = clonedDoc.getElementById('azura-pitch-page-2');
            if (p2) {
              p2.style.position = 'static';
              p2.style.visibility = 'visible';
              p2.style.opacity = '1';
            }
          }
        });
      };

      const canvas1 = await capturePage(page1El);
      const canvas2 = await capturePage(page2El);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Page 1: Guaranteed fit on exact 210mm x 297mm A4 page
      const imgData1 = canvas1.toDataURL('image/png');
      pdf.addImage(imgData1, 'PNG', 0, 0, 210, 297, undefined, 'FAST');

      // Page 2: Guaranteed fit on exact 210mm x 297mm A4 page
      pdf.addPage();
      const imgData2 = canvas2.toDataURL('image/png');
      pdf.addImage(imgData2, 'PNG', 0, 0, 210, 297, undefined, 'FAST');

      pdf.save('Azura_Seraph_AzuraOS_Pitch_Deck_Raksha_Singh.pdf');
      if (showToast) showToast('✅ 2-Page AzuraOS Pitch Deck PDF downloaded with zero page cuts!', 'success');
    } catch (err) {
      console.error('PDF export failed:', err);
      if (showToast) showToast('⚠️ Direct PDF export issue, opening print dialog...', 'info');
      window.print();
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-fade-in overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#111314] border border-[#14B8A6]/40 rounded-3xl max-w-5xl w-full p-4 sm:p-7 space-y-4 relative shadow-2xl text-white my-auto">
        
        {/* TOP HEADER */}
        <div className="flex items-center justify-between gap-3 border-b border-[#1F2426] pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] flex items-center justify-center shrink-0 shadow-inner">
              <Presentation size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-extrabold text-white tracking-tight">{effectiveTitle}</h2>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-[#14B8A6]/20 text-[#14B8A6] rounded border border-[#14B8A6]/40">
                  INVESTOR &amp; ACCELERATOR DECK
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Elevator Pitch, Cohort Governance &amp; Full 10-Slide Deck • Raksha Singh (Founder &amp; Principal Architect)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyFullBriefing}
              className="px-3 py-1.5 bg-[#171B1D] hover:bg-[#202629] text-gray-200 hover:text-white border border-[#2A3135] rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Copy complete pitch deck text to clipboard"
            >
              {copiedText ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-[#14B8A6]" />}
              <span className="hidden sm:inline">{copiedText ? 'Copied Pitch Deck!' : 'Copy Text'}</span>
            </button>

            <button
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
              className="px-3.5 py-1.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs transition flex items-center gap-1.5 cursor-pointer shadow-md shadow-[#14B8A6]/20 active:scale-95 disabled:opacity-50"
              title="Download formal pitch deck PDF"
            >
              <Download size={14} />
              <span>{isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}</span>
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-[#1F2426] transition cursor-pointer shrink-0 border border-transparent hover:border-[#2A2F31]"
              title="Close pitch deck (Esc)"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* SLIDES PRESENTATION */}
        <div className="space-y-4">
          
          {/* Top Toolbar: Navigation & Category Navigator */}
          <div className="flex flex-wrap items-center justify-between gap-2 bg-[#0C0E0F] p-2.5 rounded-xl border border-[#2A2F31]">
            {/* Slide Category Navigator */}
            <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 scrollbar-thin max-w-2xl">
              {activeSlides.map((slide, idx) => (
                <button
                  key={slide.number}
                  onClick={() => setCurrentSlideIndex(idx)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold transition whitespace-nowrap cursor-pointer ${
                    currentSlideIndex === idx
                      ? 'bg-[#14B8A6] text-black font-extrabold shadow-sm'
                      : 'bg-[#151819] text-gray-400 hover:text-gray-200 border border-[#2A2F31]'
                  }`}
                >
                  {slide.number}. {slide.category}
                </button>
              ))}
            </div>

            {/* Slide Counter */}
            <div className="px-2.5 py-1 bg-[#151819] rounded-lg border border-[#2A2F31] text-[11px] font-mono text-gray-400 shrink-0">
              Slide <span className="text-[#14B8A6] font-bold">{currentSlideIndex + 1}</span> of {totalSlides}
            </div>
          </div>

          {/* MAIN SLIDE CARD (16:9 Presentation Stage) */}
          <div 
            ref={slideCardRef}
            className="bg-gradient-to-br from-[#151819] via-[#0C0E0F] to-[#121617] border border-[#2A2F31] rounded-2xl p-6 sm:p-8 space-y-5 shadow-2xl relative overflow-hidden animate-fade-in"
          >
            {/* Ambient Slide Badge */}
            <div className="flex items-center justify-between">
              <span className="px-3 py-1 rounded-full text-[10px] font-mono font-extrabold uppercase bg-teal-500/10 text-teal-400 border border-teal-500/30 flex items-center gap-1.5">
                <Sparkles size={11} />
                <span>SLIDE {currentSlide.number} OF {totalSlides} • {currentSlide.category.toUpperCase()}</span>
              </span>

              <span className="text-[11px] font-mono text-gray-500 font-medium">
                {currentSlideIndex + 1} / {totalSlides}
              </span>
            </div>

            {/* Slide Heading & Tagline */}
            <div className="space-y-1.5">
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                {currentSlide.title}
              </h3>
              <p className="text-sm sm:text-base text-teal-300 font-medium leading-relaxed">
                {currentSlide.tagline}
              </p>
            </div>

            {/* Key Strategic Insights */}
            <div className="space-y-2.5 bg-[#0C0E0F]/90 p-4 sm:p-5 rounded-2xl border border-[#1F2426]">
              <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider font-mono">
                Core Strategic Architecture &amp; Execution Narrative
              </h4>
              <div className="space-y-2.5">
                {currentSlide.keyPoints.map((point, pIdx) => (
                  <div key={pIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-gray-200 leading-relaxed">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#14B8A6] mt-2 shrink-0 shadow-sm" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics / Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {currentSlide.metricsOrHighlights.map((m, mIdx) => (
                <div key={mIdx} className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-0.5 shadow-sm">
                  <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider font-mono">
                    {m.label}
                  </div>
                  <div className="text-base sm:text-lg font-extrabold text-white font-mono">
                    {m.value}
                  </div>
                  {m.sub && (
                    <div className="text-[10px] text-teal-400/90 font-mono">
                      {m.sub}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Slide Takeaway Callout */}
            <div className="p-3.5 bg-gradient-to-r from-teal-500/10 to-transparent border-l-4 border-[#14B8A6] rounded-r-xl text-xs text-gray-300 italic">
              <strong className="text-white not-italic font-bold">Key Strategic Takeaway:</strong> &ldquo;{currentSlide.takeaway}&rdquo;
            </div>
          </div>

          {/* Footer Navigation Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between pt-2 border-t border-[#1F2426] gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                disabled={currentSlideIndex === 0}
                className="px-4 py-2 bg-[#0C0E0F] hover:bg-[#1F2426] disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
              >
                <ChevronLeft size={15} />
                <span>Previous</span>
              </button>

              <button
                onClick={handleNext}
                disabled={currentSlideIndex === totalSlides - 1}
                className="px-4 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] disabled:opacity-30 disabled:cursor-not-allowed text-black font-extrabold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-lg"
              >
                <span>Next</span>
                <ChevronRight size={15} />
              </button>
            </div>

            <div className="text-[11px] text-gray-400 text-center font-mono">
              <span className="text-teal-400 font-bold">Slide {currentSlideIndex + 1} of {totalSlides}</span>
              <span className="text-gray-600 mx-2">•</span>
              <span className="text-gray-400 font-medium">Investor &amp; Accelerator Diligence Pack</span>
            </div>

            <div className="text-[10px] text-gray-400 font-mono">
              {effectiveCompany}
            </div>
          </div>

        </div>

      </div>

      {/* 📄 CALIBRATED OFF-SCREEN CANVAS FOR HIGH-RESOLUTION 2-PAGE A4 PDF GENERATION */}
      <div 
        id="azura-pitch-deck-pdf-canvas-wrapper" 
        className="fixed -left-[9999px] -top-[9999px] overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        {/* PAGE 1: A4 (210mm x 297mm) */}
        <div 
          id="azura-pitch-page-1"
          style={{ 
            width: '210mm', 
            height: '297mm', 
            maxHeight: '297mm', 
            boxSizing: 'border-box', 
            padding: '13mm 16mm', 
            backgroundColor: '#ffffff', 
            color: '#0f172a', 
            fontFamily: 'system-ui, -apple-system, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          {/* Header */}
          <div style={{ borderBottom: '2px solid #0d9488', paddingBottom: '10px' }} className="flex justify-between items-start">
            <div>
              <div style={{ fontSize: '17px', fontWeight: 'bold', color: '#0f172a', letterSpacing: '-0.4px' }}>
                AZURA SERAPH™ // AZURAOS INVESTOR PITCH DECK
              </div>
              <div style={{ fontSize: '11px', color: '#0d9488', fontWeight: '600', marginTop: '2px' }}>
                Sovereign Financial Operating System &amp; Cohort Governance Hub
              </div>
              <div style={{ fontSize: '9.5px', color: '#64748b', marginTop: '2px' }}>
                Founder &amp; Principal Architect: Raksha Singh • Azura Seraph™ Pty Ltd • Live System: https://azuraos.app
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '9px', color: '#64748b', lineHeight: '1.4' }}>
              <div><strong>Status:</strong> Live Production</div>
              <div><strong>Google for Startups Cloud Program</strong> ($2k+)</div>
              <div><strong>F6S Accelerator Deal Partner</strong></div>
              <div><strong>Merchant of Record:</strong> Dodo Payments</div>
            </div>
          </div>

          {/* Section A: 30-Second Elevator Pitch */}
          <div style={{ backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '7px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10.5px', fontWeight: 'bold', color: '#0f766e', textTransform: 'uppercase', marginBottom: '3px' }}>
              [A] 30-Second Elevator Pitch
            </div>
            <p style={{ fontSize: '9.5px', color: '#134e4a', margin: 0, lineHeight: '1.45' }}>
              Legacy accounting tools look in the rearview mirror, leaving statutory tax liabilities co-mingled in operating cash until founders face sudden cash depletion. AzuraOS is a live forward solvency radar that automatically ring-fences multi-jurisdiction tax reserves (IRS, HMRC, EU VAT, SARS, ATO) and recalculates True Safe Spendable Draw in real-time before committing a dollar. For accelerators, our 25-seat cohort dashboard gives program directors real-time batch visibility and audit exports without violating founder privacy.
            </p>
          </div>

          {/* Section B: 2-Minute Cohort Governance Pitch */}
          <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10.5px', fontWeight: 'bold', color: '#334155', textTransform: 'uppercase', marginBottom: '3px' }}>
              [B] 2-Minute Cohort Governance Pitch
            </div>
            <p style={{ fontSize: '9.5px', color: '#334155', margin: 0, lineHeight: '1.45' }}>
              If you run an accelerator or manage startup batches, tracking portfolio financial health is a manual nightmare of outdated spreadsheets. For batch founders, spending blindly without ring-fencing tax reserves kills runway. AzuraOS fixes both sides: one $750 pilot provisions 25 founders with complete multi-currency invoicing, client agreements, and AI statement reconciliation, while the program director tracks real-time burn and revenue milestones. Backed by Google for Startups and live on F6S, AzuraOS turns bookkeeping into an active survival shield.
            </p>
          </div>

          {/* Section C: Investor Pitch Deck (Part 1: Sections 1-3) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', borderBottom: '1px solid #cbd5e1', paddingBottom: '3px', textTransform: 'uppercase' }}>
              [C] Full 10-Slide Investor Pitch Deck (Part 1)
            </div>

            {/* 1. Hook & Problem */}
            <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '9px' }}>
              <div style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '10.5px' }}>1. The Rearview Mirror Trap &amp; Silent Runway Bleed</div>
              <p style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', marginBottom: '3px', lineHeight: '1.45' }}>
                When revenue arrives, founders see a swollen bank balance and spend on growth, unaware that 25–40% is already owed to tax authorities. They discover their true shortfall months later when tax assessments strike.
              </p>
              <p style={{ fontSize: '9.5px', color: '#334155', margin: 0, lineHeight: '1.45' }}>
                Simultaneously, accelerator program directors have zero real-time visibility into the cash survival or burn rate of their batch startups. Traditional software like QuickBooks is built for CPAs looking backward, forcing founders to juggle 4–6 siloed apps with zero forward solvency radar.
              </p>
            </div>

            {/* 2. Solution & Platform */}
            <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '9px' }}>
              <div style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '10.5px' }}>2. Live Forward Solvency &amp; Batch Governance</div>
              <p style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', marginBottom: '3px', lineHeight: '1.45' }}>
                AzuraOS replaces retroactive accounting with live forward solvency. Our engine dynamically ring-fences multi-jurisdiction statutory reserves (IRS, HMRC, EU VAT, SARS, ATO) and computes the founder&apos;s True Safe Spendable Draw before committing a single dollar.
              </p>
              <p style={{ fontSize: '9.5px', color: '#334155', margin: 0, lineHeight: '1.45' }}>
                Program directors monitor aggregated cohort burn rates and milestones from one centralized dashboard, while founders access legally binding contracts, proforma invoicing, and AI statement reconciliation.
              </p>
            </div>

            {/* 3. Multi-AI Architecture */}
            <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '9px' }}>
              <div style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '10.5px' }}>3. Multi-AI Provider BYOK &amp; Air-Gapped Engine</div>
              <p style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', marginBottom: '3px', lineHeight: '1.45' }}>
                Enterprise teams and power founders plug in their own API keys across Google Gemini, Anthropic Claude, OpenAI, and DeepSeek, backed by an intelligent server AI dispatcher with automated failover and 100% offline local heuristic fallbacks.
              </p>
              <p style={{ fontSize: '9.5px', color: '#334155', margin: 0, lineHeight: '1.45' }}>
                Our air-gapped dual-vault parser ingests bank statements in under 0.05s with zero credential scraping (No-Plaid security), ensuring absolute cryptographic data sovereignty.
              </p>
            </div>
          </div>

          {/* Page 1 Footer */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '6px', fontSize: '8.5px', color: '#94a3b8' }} className="flex justify-between items-center">
            <div>Page 1 of 2 • Azura Seraph™ Pty Ltd • Confidential Investor &amp; Accelerator Diligence</div>
            <div>https://azuraos.app • founder@azuraos.app</div>
          </div>
        </div>

        {/* PAGE 2: A4 (210mm x 297mm) */}
        <div 
          id="azura-pitch-page-2"
          style={{ 
            width: '210mm', 
            height: '297mm', 
            maxHeight: '297mm', 
            boxSizing: 'border-box', 
            padding: '13mm 16mm', 
            backgroundColor: '#ffffff', 
            color: '#0f172a', 
            fontFamily: 'system-ui, -apple-system, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          {/* Page 2 Running Header */}
          <div style={{ borderBottom: '2px solid #0d9488', paddingBottom: '10px' }} className="flex justify-between items-start">
            <div>
              <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#0f172a', letterSpacing: '-0.3px' }}>
                AZURA SERAPH™ // AZURAOS INVESTOR PITCH DECK
              </div>
              <div style={{ fontSize: '11px', color: '#0d9488', fontWeight: '600', marginTop: '2px' }}>
                Full 10-Slide Investor Pitch Deck (Continued) • Page 2 of 2
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '9px', color: '#64748b' }}>
              <div><strong>Founder:</strong> Raksha Singh</div>
              <div><strong>Live Production:</strong> https://azuraos.app</div>
              <div><strong>Target Round:</strong> $500,000 Pre-Seed</div>
            </div>
          </div>

          {/* Section C Continued: Sections 4-7 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            
            {/* 4. Distribution & Moat */}
            <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '9px' }}>
              <div style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '10.5px' }}>4. B2B2C Accelerator Distribution Flywheel &amp; Moat</div>
              <p style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', marginBottom: '3px', lineHeight: '1.45' }}>
                Instead of expensive D2C ads, we sell directly to accelerators and incubators. One $750 pilot deal provisions 25 active startups on Day 1. When startups graduate, AzuraOS remains their permanent operating system ($29–$79/mo).
              </p>
              <p style={{ fontSize: '9.5px', color: '#334155', margin: 0, lineHeight: '1.45' }}>
                Our 3-layer moat combines institutional cohort distribution, multi-jurisdiction tax reserve logic, and cryptographic offline data sovereignty.
              </p>
            </div>

            {/* 5. Business Model and Financials */}
            <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '9px' }}>
              <div style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '10.5px' }}>5. Business Model &amp; Unit Economics</div>
              <p style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', margin: 0, lineHeight: '1.45' }}>
                Dual revenue model: $750 upfront 30-day cohort pilots (up to 25 seats, listed on F6S) + $29–$79/mo post-graduation founder SaaS subscriptions. Client-side encryption and Google Cloud serverless architecture deliver &gt;85% gross margins with negligible token overhead.
              </p>
            </div>

            {/* 6. Team */}
            <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '9px' }}>
              <div style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '10.5px' }}>6. Founder &amp; Execution Velocity</div>
              <p style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', marginBottom: '3px', lineHeight: '1.45' }}>
                Raksha Singh, Founder and Principal Architect behind AzuraOS. Combining military systems analysis, luxury hospitality operations, and full-stack software development to build lean, zero-bloat financial infrastructure.
              </p>
              <p style={{ fontSize: '9.5px', color: '#334155', margin: 0, lineHeight: '1.45' }}>
                100% bootstrapped with a clean cap table and zero dilutive debt. Actively evaluating candidates for a Partnerships Lead and Systems Engineer to accelerate distribution.
              </p>
            </div>

            {/* 7. The Ask & Road to May 2027 */}
            <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '9px' }}>
              <div style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '10.5px' }}>7. The Ask &amp; Scaling Roadmap to May 2027</div>
              <p style={{ fontSize: '9.5px', color: '#334155', marginTop: '2px', margin: 0, lineHeight: '1.45' }}>
                Raising $500,000 Pre-Seed capital to scale B2B accelerator partnerships, developer banking integrations (Open Banking / Plaid), and cohort customer success. Targeting 25–35 accelerator cohorts (600+ active startups) and $25k–$40k MRR ($300k–$480k ARR) by May 2027.
              </p>
            </div>

          </div>

          {/* Institutional Highlights Card */}
          <div style={{ backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '10px 14px' }}>
            <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#0f766e', textTransform: 'uppercase', marginBottom: '6px' }}>
              Institutional Architecture &amp; Unit Economics Summary
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', fontSize: '9px' }}>
              <div>
                <strong style={{ color: '#0f172a' }}>Air-Gapped Sovereignty</strong>
                <p style={{ color: '#134e4a', margin: '2px 0 0', lineHeight: '1.35' }}>0.05s client-side parsing • Zero bank credential scraping • Multi-AI BYOK (Gemini, Claude, OpenAI, DeepSeek)</p>
              </div>
              <div>
                <strong style={{ color: '#0f172a' }}>Defended Unit Margins</strong>
                <p style={{ color: '#134e4a', margin: '2px 0 0', lineHeight: '1.35' }}>&gt;85% gross margin • $750 cohort pilots • $29–$79/mo SaaS • $25k–$40k MRR roadmap target</p>
              </div>
              <div>
                <strong style={{ color: '#0f172a' }}>Ecosystem Validation</strong>
                <p style={{ color: '#134e4a', margin: '2px 0 0', lineHeight: '1.35' }}>Google for Startups Cloud Program ($2k+) • Live F6S Accelerator Deal • Dodo Payments MoR</p>
              </div>
            </div>
          </div>

          {/* Page 2 Footer */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '6px', fontSize: '8.5px', color: '#94a3b8' }} className="flex justify-between items-center">
            <div>Page 2 of 2 • © 2026 Azura Seraph™ Pty Ltd (Reg: 2024/560516/07) • Confidential Investor Submission</div>
            <div>https://azuraos.app • Contact: founder@azuraos.app</div>
          </div>
        </div>
      </div>

    </div>
  );
}
